<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { authStore } from '../authStore';
  import { Link } from 'svelte-routing';
  import { t } from '../i18nStore';

  export let id; 

  let profile = null;
  let isLoading = true;
  let error = '';
  
  let unlocking = false;
  let unlockError = '';
  
  // Revealed Data State
  let revealedEmail = null;
  let revealedIM = null;
  let trackingCode = null;
  
  let sourceCardName = null; // Store the origin card name
  
  // Reactively track the current user ID and check if they are viewing their own profile
  $: currentUserId = $authStore?.user?.idTokenClaims?.oid || $authStore?.user?.localAccountId;
  $: isSelf = currentUserId === id;

  async function loadProfile() {
      // Extract sourceCardName from URL if user navigated from the Card Holders page
      const urlParams = new URLSearchParams(window.location.search);
      sourceCardName = urlParams.get('card');

      try {
          const viewerId = $authStore?.user?.idTokenClaims?.oid || $authStore?.user?.localAccountId || '';
          const res = await fetch(getApiUrl(`/user/public/${id}?viewerId=${viewerId}`));
          if (!res.ok) throw new Error("User not found");
          profile = await res.json();
          
          if (profile.alreadyUnlockedEmail) revealedEmail = profile.email;
          if (profile.alreadyUnlockedIM) {
              revealedIM = { phone: profile.phoneNumber, whatsapp: profile.whatsapp, wechat: profile.wechat };
          }
      } catch (e) {
          error = e.message;
      } finally {
          isLoading = false;
      }
  }

  onMount(() => {
      setTimeout(loadProfile, 150);
  });

  async function handleUnlock(type) {
      if (!$authStore || !$authStore.user) {
          alert($t('public_profile.login_required') || "Please login to connect with users.");
          return;
      }

      const cost = type === 'email' ? 10 : 20;
      const confirmMsg = ($t('public_profile.confirm_deduct') || `This will deduct {cost} credits from your balance. Proceed?`).replace('{cost}', cost);
      if (!confirm(confirmMsg)) return;

      unlocking = true;
      unlockError = '';

      const currentUserId = $authStore.user.idTokenClaims?.oid || $authStore.user.localAccountId;

      try {
          const res = await authFetch(getApiUrl('/user/connect'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: currentUserId, targetUserId: id, unlockType: type })
          });
          
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || "Failed to unlock contact");

          trackingCode = data.trackingCode;
          
          if (type === 'email') {
              revealedEmail = data.email;
          } else {
              revealedIM = { phone: data.phoneNumber, whatsapp: data.whatsapp, wechat: data.wechat };
          }
      } catch (e) {
          unlockError = e.message;
      } finally {
          unlocking = false;
      }
  }
</script>

<div class="profile-container">
  <!-- Navigation Back Link -->
  {#if sourceCardName}
      <Link to={`/cardholders/${encodeURIComponent(sourceCardName)}`} class="back-link">
          {($t('public_profile.back_to_cardholders') || '← Back to Card Holders ({card})').replace('{card}', sourceCardName)}
      </Link>
  {/if}

  {#if isLoading}
      <div class="loading">{$t('public_profile.loading') || 'Loading profile...'}</div>
  {:else if error}
      <div class="error">{error}</div>
  {:else if profile}
      <div class="header-card">
          <div class="avatar-large">{profile.displayName ? profile.displayName.charAt(0).toUpperCase() : '?'}</div>
          <h1>{profile.displayName || $t('public_profile.anonymous') || 'Anonymous User'}</h1>
      </div>

      <div class="content-grid">
          <!-- Left Column: Credit Cards -->
          <div class="card panel">
              <h3>{$t('public_profile.owned_cards') || 'Owned Credit Cards'}</h3>
              {#if profile.ownedCreditCards && profile.ownedCreditCards.length > 0}
                  <ul class="card-list">
                      {#each profile.ownedCreditCards as card}
                          <li>💳 {card}</li>
                      {/each}
                  </ul>
              {:else}
                  <p class="text-muted">{$t('public_profile.no_cards') || "This user hasn't listed any credit cards yet."}</p>
              {/if}
          </div>

          <!-- Right Column: Connections -->
          <div class="card panel connection-panel">
              <h3>{$t('public_profile.connect') || 'Connect'}</h3>
              
              {#if unlockError}
                  <div class="alert error">{unlockError}</div>
              {/if}

              <!-- Email Unlock Section -->
              <div class="unlock-box">
                  <div class="info-row">
                      <span class="icon">✉️</span>
                      <div class="details">
                          <strong>{$t('public_profile.email_address') || 'Email Address'}</strong>
                          {#if revealedEmail}
                              <span class="revealed-text">{revealedEmail}</span>
                              {#if profile.alreadyUnlockedEmail}
                                  <div class="already-unlocked-badge">{$t('public_profile.previously_unlocked') || '✓ Previously Unlocked'}</div>
                              {/if}
                          {:else if profile.hasEmail}
                              <span class="masked-text">{$t('public_profile.hidden_10_credits') || 'Hidden (Requires 10 Credits)'}</span>
                          {:else}
                              <span class="masked-text">{$t('public_profile.not_provided') || 'Not provided by user'}</span>
                          {/if}
                      </div>
                  </div>
                  {#if profile.hasEmail && !revealedEmail}
                      <button class="btn unlock-btn" 
                              disabled={unlocking || isSelf} 
                              on:click={() => handleUnlock('email')}
                              title={isSelf ? "You cannot unlock your own profile" : ""}>
                          {#if isSelf}
                              {$t('public_profile.cannot_unlock_self') || 'Cannot unlock own profile'}
                          {:else}
                              {$t('public_profile.pay_10_reveal') || 'Pay 10 Credits to Reveal'}
                          {/if}
                      </button>
                  {/if}
              </div>

              <!-- IM Unlock Section -->
              <div class="unlock-box">
                  <div class="info-row">
                      <span class="icon">💬</span>
                      <div class="details">
                          <strong>{$t('public_profile.phone_im') || 'Phone & Instant Messaging'}</strong>
                          {#if revealedIM}
                              <div class="revealed-text multi-line">
                                  {#if revealedIM.phone}<div>📞 {revealedIM.phone}</div>{/if}
                                  {#if revealedIM.whatsapp}<div>🟢 WA: {revealedIM.whatsapp}</div>{/if}
                                  {#if revealedIM.wechat}<div>💬 WeChat: {revealedIM.wechat}</div>{/if}
                              </div>
                              {#if profile.alreadyUnlockedIM}
                                  <div class="already-unlocked-badge">{$t('public_profile.previously_unlocked') || '✓ Previously Unlocked'}</div>
                              {/if}
                          {:else if (profile.hasPhone || profile.hasWhatsApp || profile.hasWeChat)}
                              <span class="masked-text">{$t('public_profile.hidden_20_credits') || 'Hidden (Requires 20 Credits)'}</span>
                          {:else}
                              <span class="masked-text">{$t('public_profile.not_provided') || 'Not provided by user'}</span>
                          {/if}
                      </div>
                  </div>
                  {#if (profile.hasPhone || profile.hasWhatsApp || profile.hasWeChat) && !revealedIM}
                      <button class="btn unlock-btn im-btn" 
                              disabled={unlocking || isSelf} 
                              on:click={() => handleUnlock('im')}
                              title={isSelf ? "You cannot unlock your own profile" : ""}>
                          {#if isSelf}
                              {$t('public_profile.cannot_unlock_self') || 'Cannot unlock own profile'}
                          {:else}
                              {$t('public_profile.pay_20_reveal') || 'Pay 20 Credits to Reveal'}
                          {/if}
                      </button>
                  {/if}
              </div>
          </div>
      </div>
      
      <!-- Tracking Code Footer -->
      {#if trackingCode && trackingCode !== "PREVIOUSLY-UNLOCKED"}
          <div class="tracking-footer">
              Transaction Code: <strong>{trackingCode}</strong>
          </div>
      {/if}
  {/if}
</div>

<style>
  .profile-container { max-width: 1000px; margin: 2rem auto; padding: 1rem; position: relative; min-height: 80vh; }
  
  .back-link { display: inline-block; margin-bottom: 1.5rem; color: #007bff; text-decoration: none; font-weight: 600; font-size: 0.95rem; }
  .back-link:hover { text-decoration: underline; color: #0056b3; }

  .header-card { text-align: center; margin-bottom: 2rem; }
  .avatar-large { width: 100px; height: 100px; border-radius: 50%; background: #333; color: white; display: flex; align-items: center; justify-content: center; font-size: 3rem; font-weight: bold; margin: 0 auto 1rem; }
  h1 { margin: 0; color: #111; }

  .content-grid { display: grid; grid-template-columns: 1fr; gap: 2rem; }
  @media(min-width: 768px) { .content-grid { grid-template-columns: 1fr 1fr; } }

  .card { background: white; padding: 1.5rem; border-radius: 12px; box-shadow: 0 4px 15px rgba(0,0,0,0.05); }
  h3 { margin-top: 0; border-bottom: 2px solid #f0f0f0; padding-bottom: 0.8rem; margin-bottom: 1.5rem; color: #333; }

  .card-list { list-style: none; padding: 0; margin: 0; }
  .card-list li { padding: 0.8rem; background: #f8f9fa; margin-bottom: 0.5rem; border-radius: 6px; font-weight: 500; }

  .unlock-box { border: 1px solid #e0e0e0; border-radius: 8px; padding: 1rem; margin-bottom: 1.5rem; }
  .info-row { display: flex; align-items: flex-start; margin-bottom: 1rem; }
  .icon { font-size: 1.5rem; margin-right: 1rem; }
  .details { display: flex; flex-direction: column; }
  .details strong { color: #333; margin-bottom: 0.3rem; }
  .masked-text { color: #888; font-style: italic; font-size: 0.9rem; }
  .revealed-text { color: #28a745; font-weight: 600; font-size: 1.1rem; }
  .multi-line div { margin-top: 0.3rem; }

  .already-unlocked-badge { font-size: 0.75rem; color: #28a745; margin-top: 0.5rem; font-weight: bold; }

  .btn { width: 100%; padding: 0.8rem; border: none; border-radius: 6px; font-weight: bold; cursor: pointer; transition: opacity 0.2s; }
  .unlock-btn { background: #007bff; color: white; }
  .im-btn { background: #6f42c1; color: white; }
  .btn:hover:not(:disabled) { opacity: 0.9; }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .alert.error { background: #f8d7da; color: #721c24; padding: 1rem; border-radius: 4px; margin-bottom: 1rem; }

  .tracking-footer { position: fixed; bottom: 20px; right: 20px; background: rgba(0,0,0,0.8); color: white; padding: 0.8rem 1.5rem; border-radius: 30px; font-family: monospace; font-size: 0.9rem; box-shadow: 0 4px 10px rgba(0,0,0,0.2); animation: slideIn 0.3s ease-out; }
  
  @keyframes slideIn { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
</style>